import { Modal, SubmitButton } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import { AxiosError } from "axios";
import clsx from "clsx";
import {
    isAutoGeneratedName as checkIsAutoGeneratedName,
    cleanPickupLocationFromFormResponse,
    fixQuestionnaireFormFieldValues,
    FormField,
    getAddonClientMax,
    getFields,
    getGuestQuestionnaireFieldResponses,
    getGuestQuestionnaireFields,
    getItemRestrictionErrors,
    getQuestionnaireFormError,
    getRestrictionErrorsForItem,
    getSoldOutLimitError,
    hasAddonFieldsInForm,
    hasPickupFieldsInForm,
    initializeFieldResponse,
    InputField,
    isPast,
    ItemRestrictionErrors,
    omitRestrictionError,
    PurchaseActionOption,
    PurchasePriceStrategy,
    RadioField,
    RestrictionErrorAnswer,
    shouldSkipQuestionnaireModal,
} from "common";
import { useFormikContext } from "formik";
import { produce } from "immer";
import { isEmpty, isNil, omit, pick, sum, uniqueId } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Except } from "type-fest";
import {
    AddonFieldResponse,
    CatalogType,
    ExperiencePurchaseItem,
    Field,
    FieldType,
    FieldWithPickupOptions,
    Form,
    FormResponse,
    Guest,
    PRECHECK_FIELD_LABELS,
    ProductCatalogItem,
    Purchase,
    PurchaseItem,
    PurchaseStatus,
} from "types";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { RefundOption } from "../Dashboard/RefundOption";
import { BooleanAddonItem } from "../PurchaseModification/Addons/BooleanAddons";
import { ChoicesAddonItem } from "../PurchaseModification/Addons/ChoicesAddons";
import { QuantityAddonItem } from "../PurchaseModification/Addons/QuantityAddons";
import { PaymentBreakdown, PriceStrategy } from "../PurchaseModification/PaymentBreakdown";
import { CheckboxField } from "./CheckboxField";
import { DateOfBirthField, getISOFormattedDateString } from "./DateOfBirthField";
import { HeightField } from "./HeightField";
import { PhotoActionButtons } from "./PhotoActionButtons";
import { PhotoLightboxModal } from "./PhotoLightboxModal";
import { PickupLocationSelectField } from "./PickupLocationSelectField";
import { SelectField } from "./SelectField";
import { TextareaField } from "./TextareaField";
import { FieldProps, TextField } from "./TextField";
import { UnitField } from "./UnitField";
import { usePhotoField } from "./usePhotoField";
import { clearAddOnRestrictionError } from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { useSeller } from "@/hooks/useAccount";
import { useModal } from "@/hooks/useVisibility";
import { getGuestIndex, isAddonOverriddenByGuests, isAnswerToAddon, isPrimaryGuest } from "@/models/Guest";
import { isCommittedPurchase } from "@/models/Purchase";
import { isPriceConfigurationUpdated } from "@/models/PurchaseItem";
import { CreatePurchaseItemState, useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface QuestionnaireModalProps {
    isOpen: boolean;
    onClose: () => void;
    form: Form;
    guest: Guest;
    onSubmit: (
        guest: Guest,
        priceStrategy?: PriceStrategy,
        nextOption?: PurchaseActionOption.COLLECT_BALANCE_OPTIONS | RefundOption.RETURN_PAYMENT,
        refundAmount?: number,
    ) => Promise<void> | void;
    disableRequiredFields?: boolean;
    isItemLevel?: boolean;
    purchase?: Purchase;
    item?: PurchaseItem | CreatePurchaseItemState;
    isSaving?: boolean;
}

export const QuestionnaireModal = ({
    isOpen,
    guest,
    form,
    disableRequiredFields,
    purchase,
    item,
    isSaving = false,
    isItemLevel = false,
    onClose,
    onSubmit,
}: QuestionnaireModalProps) => {
    const handleSubmit = async (
        initGuest: Guest,
        priceStrategy?: PriceStrategy,
        nextOption?: PurchaseActionOption.COLLECT_BALANCE_OPTIONS | RefundOption.RETURN_PAYMENT,
        refundAmount?: number,
    ) => {
        const guest = { ...omit(initGuest, "checkInCount") };
        await onSubmit(guest, priceStrategy, nextOption, refundAmount);
        onClose();
    };

    const hasAddonFields = hasAddonFieldsInForm(form);
    const hasPickupResponses = guest.formResponse?.fields?.some((field) => field.type === FieldType.PICKUP);
    const hasPickupFields = (isItemLevel && hasPickupFieldsInForm(form)) || hasPickupResponses;

    return (
        <Modal
            isOpen={isOpen}
            size={(hasAddonFields || hasPickupFields) && isCommittedPurchase(purchase) ? "huge" : "medium"}
            className="ui-questionnaire-modal"
            onClose={onClose}
        >
            <Modal.Header>Questionnaire</Modal.Header>

            <ModalContent
                form={form}
                guest={guest}
                disableRequiredFields={disableRequiredFields}
                purchase={purchase}
                item={item}
                isSaving={isSaving}
                isItemLevel={isItemLevel}
                onClose={onClose}
                onSubmit={handleSubmit}
            />
        </Modal>
    );
};

const shouldRemovePickupField = (field: { type: FieldType; value: string }) =>
    field.type === FieldType.PICKUP && (!field.value || field.value.trim() === "" || field.value === "__remove__");

const getPayload = (
    values: Guest,
    purchaseItem: CreatePurchaseItemState | PurchaseItem,
    purchase: Purchase,
    showRetainPriceToggle: boolean,
    retainPrice: boolean,
    isItemLevel: boolean,
) => {
    let item: any;
    if (purchaseItem.form?.organizerOnly || isItemLevel) {
        const filteredFields = values.formResponse?.fields?.filter((field) => {
            if (field.type === FieldType.DATE_OF_BIRTH) {
                return false;
            }

            if (shouldRemovePickupField(field)) {
                return false;
            }

            return true;
        });

        const updatedFormResponse = produce(purchaseItem.formResponse ?? { fields: [] }, (draft) => {
            draft.fields = filteredFields ?? [];
        });

        item = {
            id: purchaseItem.id,
            formResponse: { ...updatedFormResponse, name: values.name || purchase.customerName },
        };
    } else {
        const cleanedValues = cleanPickupLocationFromFormResponse(values);
        const guests = [omit(cleanedValues, ["waiver", "checkInCount"])];
        item = { id: purchaseItem.id, guests };
    }
    if (showRetainPriceToggle && retainPrice) {
        item._priceStrategy = PriceStrategy.RETAIN;
    }

    return { items: [item] };
};

const ModalContent = ({
    form,
    guest,
    disableRequiredFields,
    purchase,
    item,
    isSaving,
    isItemLevel = false,
    onClose,
    onSubmit,
}: Except<QuestionnaireModalProps, "isOpen">) => {
    const purchaseItem = item ?? purchase?.items?.find((item) => item.guests?.find((g) => g.id === guest.id));
    const activeItem = useCreatePurchaseStore((state) => state.getActiveItem());
    const selectedItem = purchaseItem ?? activeItem;
    const [nameFieldId] = useState(uniqueId("NameField"));
    const storeRestrictionErrors = useCreatePurchaseStore((state) => {
        return state.getAddOnRestrictionErrors(state.activeItemIndex);
    });
    const [savedRestrictionErrors, setSavedRestrictionErrors] = useState<ItemRestrictionErrors>();
    // The store holds limit errors from the create flow only. An existing purchase keeps the errors from its own save.
    const itemRestrictionErrors = purchaseItem ? savedRestrictionErrors : storeRestrictionErrors;
    const restrictionErrorsKey = useCreatePurchaseStore((state) => {
        return state.getAddOnRestrictionErrorsKey(state.activeItemIndex);
    });
    const guests = selectedItem?.guests;
    const guestIndex = getGuestIndex(guests, guest);
    const otherGuests = useMemo(() => guests?.filter((other) => other.id !== guest.id) ?? [], [guests, guest.id]);
    const arrivalDate = (selectedItem as ExperiencePurchaseItem | CreatePurchaseItemState | undefined)?.arrivalDate;
    // Only the create flow's toggle ignores the questionnaire; an existing purchase always relaxes required fields.
    const isQuestionnaireIgnored = !purchaseItem && Boolean(disableRequiredFields);
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [retainPrice, setRetainPrice] = useState(false);
    const [updatedPurchase, setUpdatedPurchase] = useState<Purchase | null>(null);

    const isAutoGeneratedName = checkIsAutoGeneratedName(guest.name);
    const seller = useSeller();

    const isPrimary = isPrimaryGuest(guest, purchase, selectedItem);
    const questionnaireFields = useMemo(() => {
        // When item-level, use item.formResponse as the source so getGuestQuestionnaireFields
        // doesn't append extra fields sourced from guest.formResponse
        const guestForFields = isItemLevel ? { ...guest, formResponse: purchaseItem?.formResponse } : guest;
        const fields = getGuestQuestionnaireFields(form, guestForFields, isPrimary, isItemLevel) as Field[];
        const formResponseToUse = isItemLevel ? purchaseItem?.formResponse : guest.formResponse;

        const identityVerification = seller?.preferences?.payment?.fraud?.identityVerification;
        const isPrecheckEnabled =
            identityVerification?.enabled === true &&
            (identityVerification.selectedExperiences?.all === true ||
                identityVerification.selectedExperiences?.experiences?.includes(form.product?.id ?? ""));

        const precheckLabels = new Set(Object.values(PRECHECK_FIELD_LABELS).map((l) => l.toLowerCase()));

        const filteredFields = fields.filter((field) => {
            // When precheck is enabled, hide organizer-only precheck fields from the primary guest-level view.
            // They are only relevant in the item-level questionnaire.
            // Guest-level fields whose label happens to match a precheck name are not organizerOnly and are kept.
            if (
                isPrecheckEnabled &&
                !isItemLevel &&
                isPrimary &&
                field.organizerOnly &&
                precheckLabels.has(field.label?.toLowerCase())
            ) {
                return false;
            }

            return form.organizerOnly ? true : !shouldSkipQuestionnaireModal(field, formResponseToUse, isItemLevel);
        });

        // If a legacy TEXT field shares a label with a rendered PICKUP field,
        // keep it right after pickup as read-only.
        const pickupField = filteredFields.find((f) => f.type === FieldType.PICKUP);
        const normalizeLabel = (label?: string) => (label ?? "").replace(/\s+/g, "").trim().toLowerCase();
        const legacyTextField =
            pickupField &&
            !form.fields.some(
                (formField) =>
                    formField.type === FieldType.TEXT &&
                    normalizeLabel(formField.label) === normalizeLabel(pickupField.label),
            ) &&
            filteredFields.find(
                (f) =>
                    f.type === FieldType.TEXT &&
                    f.label === pickupField.label &&
                    !form.fields.some((formField) => formField.id === f.id),
            );

        if (pickupField && legacyTextField) {
            const pickupIndex = filteredFields.indexOf(pickupField);
            const textIndex = filteredFields.indexOf(legacyTextField);
            filteredFields.splice(textIndex, 1);
            filteredFields.splice(pickupIndex + 1, 0, {
                ...legacyTextField,
                isLegacyPickupTextField: true,
            } as Field);
        }

        return filteredFields;
    }, [form, guest, isPrimary, purchaseItem, isItemLevel, seller]);

    /** An existing purchase saves only this item, which the API keys by its id or as position 0. */
    const getSavedRestrictionErrors = (error: unknown) => {
        return purchaseItem && getRestrictionErrorsForItem(getItemRestrictionErrors(error), purchaseItem.id, 0);
    };

    const handlePreviewAddOnLimitError = (error: AxiosError) => {
        setSavedRestrictionErrors(getSavedRestrictionErrors(error));
    };

    const handleRestrictionErrorClear = useMemoizedFn((templateId?: ID, answer?: RestrictionErrorAnswer) => {
        if (purchaseItem) {
            setSavedRestrictionErrors((current) => current && omitRestrictionError(current, templateId, answer));
            return;
        }

        clearAddOnRestrictionError(restrictionErrorsKey, templateId, answer);
    });

    const addonLimitContext = useMemo<AddonLimitContext>(() => {
        return {
            guestIndex,
            isQuestionnaireIgnored,
            restrictionErrors: itemRestrictionErrors,
            isCommitted: isCommittedPurchase(purchase),
            onRestrictionErrorClear: handleRestrictionErrorClear,
            isPastDated: arrivalDate ? isPast(arrivalDate) : false,
            quantitiesByOthers: getQuantitiesByOthers(questionnaireFields, otherGuests),
        };
    }, [
        guestIndex,
        otherGuests,
        questionnaireFields,
        itemRestrictionErrors,
        arrivalDate,
        isQuestionnaireIgnored,
        purchase,
        handleRestrictionErrorClear,
    ]);

    const initialValues = useMemo(() => {
        const rawInitialValues = getInitialValues(form, guest, purchase, selectedItem, isItemLevel);
        const values = reorderFormResponseFields(rawInitialValues, questionnaireFields);

        // All attendees share an add-on's override, so it opens on when another attendee turned it on.
        return produce(values, (draft) => {
            for (const [index, field] of questionnaireFields.entries()) {
                const response = draft.formResponse?.fields?.[index] as AddonFieldResponse | undefined;
                const template = field.catalogItem?.template;
                if (
                    field.type === FieldType.ADDON &&
                    response &&
                    isAddonOverriddenByGuests(otherGuests, template?.id, template?.code)
                ) {
                    response._overrideRestrictions = true;
                }
            }
        });
    }, [questionnaireFields, form, guest, purchase, selectedItem, isItemLevel, otherGuests]);

    const handleSubmit = async (values: Guest) => {
        const name = isEmpty(values.name?.trim()) ? guest.name : values.name;

        const updatedFormResponse = values.formResponse
            ? {
                  ...values.formResponse,
                  fields: values.formResponse.fields
                      .filter((field) => !shouldRemovePickupField(field))
                      .map((field) => ({ ...field })),
              }
            : undefined;

        let updateValues: Guest = {
            ...values,
            name,
            formResponse: updatedFormResponse,
        };

        // Sanitize DATE_OF_BIRTH fields to ISO format
        if (updateValues.formResponse) {
            updateValues = sanitizeFormFieldValues(updateValues);
        }

        const showCollectBalanceModal =
            purchase && updatedPurchase && updatedPurchase?.dueNow > 0 && updatedPurchase.dueNow !== purchase.dueNow;
        const showRefundModal =
            purchase && updatedPurchase && updatedPurchase?.dueNow < 0 && updatedPurchase.dueNow !== purchase.dueNow;
        const refundAmount = showRefundModal ? Math.abs(updatedPurchase.dueNow) : 0;

        let nextOption: PurchaseActionOption.COLLECT_BALANCE_OPTIONS | RefundOption.RETURN_PAYMENT | undefined;
        if (showCollectBalanceModal) {
            nextOption = PurchaseActionOption.COLLECT_BALANCE_OPTIONS;
        } else if (showRefundModal) {
            nextOption = RefundOption.RETURN_PAYMENT;
        }

        setSavedRestrictionErrors(undefined);
        try {
            await onSubmit(
                updateValues,
                showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
                nextOption,
                refundAmount,
            );
        } catch (error) {
            const errors = getSavedRestrictionErrors(error);
            if (errors) {
                setSavedRestrictionErrors(errors);
                return;
            }

            throw error;
        }
    };

    const handlePaymentBreakdownUpdate = (purchase: Purchase | null) => {
        if (isPriceConfigurationUpdated(purchase, selectedItem as PurchaseItem) && !showRetainPriceToggle) {
            setShowRetainPriceToggle(true);
            setRetainPrice(!retainPrice);
        }

        setUpdatedPurchase(purchase);
    };

    const needsName = isAutoGeneratedName || !guest.name;

    const hasAddonFields = form.fields.some((field) => field.type === FieldType.ADDON);
    const hasPickupField = questionnaireFields.some((field) => field.type === FieldType.PICKUP);
    const showPaymentBreakDown = hasAddonFields || (hasPickupField && isItemLevel);

    useEffect(() => {
        // If guest name is empty or auto-generated, we need to set the focus on `name` field for initial render
        if (needsName) {
            // TODO: Use ref when  ui-kit supports `forwardRef`
            const nameField: HTMLInputElement | null = document.querySelector(`#${nameFieldId}`);
            nameField?.focus();
            return;
        }

        // If guest name is available, we need to focus the first empty questionnaire field
        for (const [index, field] of questionnaireFields.entries()) {
            if (!field.value) {
                const selector = `input[name="formResponse.fields.${index}.value"]`;
                const inputField: HTMLElement | null = document.querySelector(selector);
                inputField?.focus();
                break;
            }
        }
    }, [needsName, nameFieldId, questionnaireFields]);

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validate={validate(
                questionnaireFields,
                disableRequiredFields ?? false,
                isAutoGeneratedName,
                selectedItem,
                isItemLevel,
            )}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit, dirty }) => {
                const addonFieldLimits = getAddonFieldLimits(
                    questionnaireFields,
                    values,
                    initialValues,
                    addonLimitContext,
                );
                const isSaveDisabled = !dirty || isAddonLimitExceeded(questionnaireFields, addonFieldLimits);

                return (
                    <>
                        <Modal.Body
                            className={clsx(
                                purchase?.status === PurchaseStatus.COMMITTED &&
                                    showPaymentBreakDown &&
                                    "grid grid-cols-2 gap-8",
                            )}
                        >
                            <div className="max-h-[70vh] overflow-y-auto">
                                <PurchasePriceStrategy
                                    isChecked={retainPrice}
                                    isVisible={showRetainPriceToggle}
                                    onChange={() => setRetainPrice(!retainPrice)}
                                />
                                <FormField label="Name" name="name" className="mt-3">
                                    <InputField
                                        id={nameFieldId}
                                        isRequired={!isAutoGeneratedName}
                                        placeholder={isAutoGeneratedName ? guest.name : undefined}
                                    />
                                </FormField>

                                {questionnaireFields.map((field, index) => {
                                    // In the case of weight and height fields, if the purchase was completed from checkout,
                                    // user can choose a different unit than the one configured, which is what we need to
                                    // show.
                                    const response = guest.formResponse?.fields?.find(
                                        (responseField) => field.label === responseField.label,
                                    ) ?? { value: "" };
                                    const isSellerUnitShouldBeUsed =
                                        field.type === FieldType.WEIGHT || field.type === FieldType.HEIGHT;

                                    // Use textarea field for large text responses.
                                    const type = field.type as FieldType;
                                    let Input = fieldMap[type] ?? TextField;

                                    if (fieldMap[type] === TextField && response.value?.length > 60) {
                                        Input = TextareaField;
                                    }

                                    if (field.type === FieldType.ADDON) {
                                        return (
                                            <AddonFormField
                                                key={field.id}
                                                index={index}
                                                field={field}
                                                limit={addonFieldLimits[index]}
                                                limitContext={addonLimitContext}
                                            />
                                        );
                                    }

                                    if (field.type === FieldType.PHOTO) {
                                        return (
                                            <PhotoFieldDisplay
                                                key={field.id}
                                                field={field}
                                                guest={guest}
                                                item={selectedItem}
                                                purchase={purchase}
                                                isItemLevel={isItemLevel}
                                            />
                                        );
                                    }

                                    if (isPickupField(field)) {
                                        return (
                                            <div
                                                key={field.id}
                                                data-testid="questionnaire-form-field"
                                                className="w-full"
                                            >
                                                <PickupLocationSelectField
                                                    isRequired={field.required}
                                                    item={selectedItem}
                                                    name={`formResponse.fields.${index}.value`}
                                                    field={{
                                                        ...field,
                                                        ...pick(response, ["unit"]),
                                                    }}
                                                    classNames={{ label: "overflow-hidden text-ellipsis no-translate" }}
                                                />
                                            </div>
                                        );
                                    }

                                    const shouldDisable = Boolean(
                                        (field as Field & { isLegacyPickupTextField?: boolean })
                                            .isLegacyPickupTextField,
                                    );

                                    return (
                                        <div key={field.id} data-testid="questionnaire-modal-form-field">
                                            <Input
                                                isRequired={field.required}
                                                disabled={shouldDisable}
                                                name={`formResponse.fields.${index}.value`}
                                                field={{
                                                    ...field,
                                                    ...(isSellerUnitShouldBeUsed ? {} : pick(response, ["unit"])),
                                                }}
                                                classNames={{ label: "overflow-hidden text-ellipsis no-translate" }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {purchase && purchase.status === PurchaseStatus.COMMITTED && showPaymentBreakDown && (
                                <div className="rounded-lg bg-gray-lighter p-6">
                                    <PaymentBreakdown
                                        purchase={purchase}
                                        payload={getPayload(
                                            values,
                                            selectedItem,
                                            purchase,
                                            showRetainPriceToggle,
                                            retainPrice,
                                            isItemLevel,
                                        )}
                                        onUpdate={handlePaymentBreakdownUpdate}
                                        onAddOnLimitError={handlePreviewAddOnLimitError}
                                    />
                                </div>
                            )}
                        </Modal.Body>

                        <Modal.Footer>
                            <SecondaryButton disabled={isSaving} onClick={onClose}>
                                Cancel
                            </SecondaryButton>

                            <SubmitButton
                                isLoading={isSaving}
                                disabled={isSaveDisabled}
                                type="submit"
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface PhotoFieldDisplayProps {
    field: Field;
    guest: Guest;
    item?: PurchaseItem | CreatePurchaseItemState;
    purchase?: Purchase;
    isItemLevel?: boolean;
}

/**
 * Displays a photo field from the questionnaire.
 * Photos can be stored at either the item level (items[].formResponse) or
 * the guest level (items[].guests[].formResponse), so we check both locations.
 * Images are fetched with authentication and displayed as blob URLs.
 */
const PhotoFieldDisplay = ({ field, guest, item, purchase, isItemLevel }: PhotoFieldDisplayProps) => {
    const modal = useModal();
    const [isHovered, setIsHovered] = useState(false);
    const { photoPath, blob, blobUrl, isLoading, isError } = usePhotoField({
        field,
        guest,
        item,
        purchase,
        isItemLevel,
    });

    const handleViewFullSize = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            modal.show();
        },
        [modal],
    );

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    if (!photoPath) {
        return <PhotoField label={field.label} placeholder="No photo uploaded" />;
    }

    if (isLoading) {
        return <PhotoField label={field.label} placeholder="Loading…" />;
    }

    if (isError || !blobUrl) {
        return <PhotoField dashed label={field.label} placeholder="No image available" />;
    }

    return (
        <PhotoField label={field.label}>
            <div className="relative overflow-hidden" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <img src={blobUrl} alt={field.label} className="max-h-64 w-full object-contain" />
                {isHovered && (
                    <div className="absolute right-6 top-2 flex gap-2">
                        <PhotoActionButtons
                            blob={blob}
                            blobUrl={blobUrl}
                            altText={field.label}
                            photoPath={photoPath}
                            buttonClassName="bg-white !p-2 shadow-md"
                            onViewFullSize={handleViewFullSize}
                        />
                    </div>
                )}
            </div>
            <PhotoLightboxModal
                isOpen={modal.isVisible}
                blobUrl={blobUrl}
                blob={blob}
                altText={field.label}
                photoPath={photoPath}
                onClose={modal.hide}
            />
        </PhotoField>
    );
};

interface PhotoFieldProps {
    label: string;
    placeholder?: string;
    dashed?: boolean;
    children?: React.ReactNode;
}

const PhotoField = ({ label, placeholder, dashed, children }: PhotoFieldProps) => (
    <div data-testid="questionnaire-photo-field" className="mb-4">
        <label className="mb-2 block text-sm font-bold text-gray-darker">{label}</label>
        {placeholder ? (
            <div
                className={clsx(
                    "bg-gray-50 flex h-32 items-center justify-center rounded-lg border",
                    dashed ? "border-gray-300 border-dashed" : "border-gray-200",
                )}
            >
                <span className="text-sm italic text-gray-dark">{placeholder}</span>
            </div>
        ) : (
            children
        )}
    </div>
);

type QuestionnaireValues = ReturnType<typeof getInitialValues>;

interface AddonLimitContext {
    /** The API skips limits for a past arrival, so the client does too. */
    isPastDated: boolean;
    /** Overrides all overridable questionnaire add-on limits, so the per-attendee switch is hidden. */
    isQuestionnaireIgnored: boolean;
    /** Only a committed purchase has booked quantities. A draft has booked nothing yet. */
    isCommitted: boolean;
    guestIndex: number;
    restrictionErrors?: ItemRestrictionErrors;
    /** What each other attendee answered for each field's add-on, by field index. */
    quantitiesByOthers: number[][];
    onRestrictionErrorClear: (templateId?: ID, answer?: RestrictionErrorAnswer) => void;
}

interface AddonFieldLimit {
    isOverriding: boolean;
    isOverCap: boolean;
    max?: number;
    templateId?: ID;
    limitError?: string;
    /** Another attendee is over a limit this switch can waive, so it shows here too. */
    isLimitExceeded: boolean;
}

const toQuantity = (value: unknown) => Number(value) || 0;

const getFieldTemplateId = (field: Field) => field.catalogItem?.template?.id ?? field.catalogItem?.id;

/** The same add-on can sit at a different index for each attendee, so match it by template. */
const getQuantitiesByOthers = (fields: Field[], otherGuests: Guest[]) => {
    return fields.map((field) => {
        if (field.type !== FieldType.ADDON) {
            return [];
        }

        const templateId = getFieldTemplateId(field);
        const templateCode = field.catalogItem?.template?.code;

        return otherGuests.map((other) => {
            const answer = other.formResponse?.fields?.find((otherField) => {
                return isAnswerToAddon(otherField, templateId, templateCode);
            });

            return toQuantity(answer?.value);
        });
    });
};

/**
 * Each attendee answers for one guest. The booking limit left for them is what the other attendees have not taken.
 * A booking or event limit error names the whole booking, so the attendee sees their own share of it instead.
 */
const getAddonFieldLimit = (
    field: Field,
    index: number,
    values: QuestionnaireValues,
    initialValues: QuestionnaireValues,
    context: AddonLimitContext,
) => {
    const templateId = getFieldTemplateId(field);
    const response = values?.formResponse?.fields?.at(index) as AddonFieldResponse | undefined;
    const value = toQuantity(response?.value);
    const quantitiesByOthers = context.quantitiesByOthers[index] ?? [];
    const isOverriding = Boolean(response?._overrideRestrictions) || context.isQuestionnaireIgnored;
    // On a committed purchase only an increase past the limit counts, so a booked quantity above a lowered limit
    // never blocks the save.
    const soldValue = context.isCommitted ? toQuantity(initialValues?.formResponse?.fields?.at(index)?.value) : 0;
    const catalogItemOpen = templateId ? context.restrictionErrors?.catalogItemOpen?.[templateId] : undefined;
    let max: number | undefined;
    let isLimitExceeded = false;
    if (field.catalogItem && !context.isPastDated) {
        const clientMax = getAddonClientMax({
            isOverriding,
            catalogItem: field.catalogItem,
            guestCount: 1,
            catalogItemOpen,
            quantitySelectedByOthers: sum(quantitiesByOthers),
        });
        max = isNil(clientMax) ? clientMax : Math.max(clientMax, soldValue);
        // The other attendees' answers on a committed purchase are already booked, and the API checks only increases.
        isLimitExceeded =
            !context.isCommitted && isOverridableLimitExceededByOthers(field.catalogItem, quantitiesByOthers, value);
    }

    const isOverCap = !isNil(max) && value > max;
    // Matches the add-on rows: Sold Out only when the event has none left, not when other attendees took the rest.
    const isSoldOut = isOverCap && catalogItemOpen === 0 && max === 0;
    const answerError = context.restrictionErrors?.guests[context.guestIndex]?.[index];

    return {
        max,
        templateId,
        isOverriding,
        isLimitExceeded,
        isOverCap,
        limitError: getSoldOutLimitError(answerError, isSoldOut),
    };
};

const isOverridableLimitExceededByOthers = (
    catalogItem: Pick<ProductCatalogItem, "restrictions">,
    quantitiesByOthers: number[],
    value: number,
) => {
    const total = sum(quantitiesByOthers) + value;
    const restrictions = catalogItem.restrictions?.filter((restriction) => restriction.allowOverride);

    return quantitiesByOthers.some((quantity) => {
        const otherMax = getAddonClientMax({
            catalogItem: { restrictions },
            guestCount: 1,
            quantitySelectedByOthers: total - quantity,
        });

        return !isNil(otherMax) && quantity > otherMax;
    });
};

const getAddonFieldLimits = (
    fields: Field[],
    values: QuestionnaireValues,
    initialValues: QuestionnaireValues,
    context: AddonLimitContext,
) => {
    return fields.map((field, index) => {
        if (field.type !== FieldType.ADDON || !field.catalogItem) {
            return undefined;
        }

        return getAddonFieldLimit(field, index, values, initialValues, context);
    });
};

const isAddonLimitExceeded = (fields: Field[], limits: Array<AddonFieldLimit | undefined>) => {
    return fields.some((field, index) => {
        const limit = limits[index];
        // Dropdown add-ons have no limits.
        if (!limit || field.catalogItem?.type === CatalogType.CHOICES) {
            return false;
        }

        return Boolean(limit.limitError) || limit.isOverCap;
    });
};

interface AddonFormFieldProps {
    index: number;
    field: Field;
    limit?: AddonFieldLimit;
    limitContext: AddonLimitContext;
}

const AddonFormField = ({ index, field, limit, limitContext }: AddonFormFieldProps) => {
    const history = useHistory();
    const { values, setFieldValue, errors } = useFormikContext<QuestionnaireValues>();

    const shouldShowDefaultImgInAddOnsList = history.location.pathname === "/cart";

    if (!field.catalogItem || !limit) {
        return null;
    }

    const response = values.formResponse?.fields?.at(index) as AddonFieldResponse;
    const error = (errors?.formResponse as any)?.fields?.[index]?.value;
    const { isOverriding, max, isOverCap, templateId, limitError, isLimitExceeded } = limit;

    // Editing the answer clears its limit error and the add-on's booking error, which no longer holds once an answer
    // changes. The store clears errors only when the next save starts.
    const hasBookingError = Boolean(templateId && limitContext.restrictionErrors?.errors[templateId]);
    const clearRestrictionError = () => {
        if (limitError || hasBookingError) {
            limitContext.onRestrictionErrorClear(templateId, {
                guestIndex: limitContext.guestIndex,
                fieldIndex: index,
            });
        }
    };

    const handleOverrideChange = (value: boolean) => {
        setFieldValue(`formResponse.fields.${index}._overrideRestrictions`, value);
        clearRestrictionError();
    };

    const overrideProps = limitContext.isQuestionnaireIgnored
        ? { isOverriding: false }
        : { isOverriding, isLimitExceeded, onOverrideChange: handleOverrideChange };

    const handleQuantityChange = (value: number) => {
        setFieldValue(`formResponse.fields.${index}.sku`, field.catalogItem?.template?.code);
        setFieldValue(`formResponse.fields.${index}.value`, value);
        clearRestrictionError();
    };

    const handleBooleanChange = (value: boolean) => {
        setFieldValue(`formResponse.fields.${index}.sku`, field.catalogItem?.template?.code);
        setFieldValue(`formResponse.fields.${index}.value`, value ? 1 : 0);
        clearRestrictionError();
    };

    const handleChoiceChange = (templatedId: string, templateCode: string) => {
        const isNoneOption = templatedId === "None";
        if (isNoneOption) {
            setFieldValue(`formResponse.fields.${index}.value`, 0);
            return;
        }
        setFieldValue(`formResponse.fields.${index}.sku`, templateCode);
        setFieldValue(`formResponse.fields.${index}.lineItemTemplate`, { id: templatedId });
        setFieldValue(`formResponse.fields.${index}.value`, 1);
    };

    if (field.catalogItem.type === CatalogType.QUANTITY) {
        return (
            <QuantityAddonItem
                shouldShowDefaultImg={shouldShowDefaultImgInAddOnsList}
                disabledSpinner={false}
                shouldDecreaseOnly={false}
                catalogItem={field.catalogItem}
                value={toQuantity(response?.value)}
                max={max}
                error={error}
                limitError={limitError}
                onChange={handleQuantityChange}
                {...overrideProps}
            />
        );
    }

    if (field.catalogItem.type === CatalogType.BOOLEAN) {
        return (
            <BooleanAddonItem
                shouldShowDefaultImg={shouldShowDefaultImgInAddOnsList}
                isChecked={toQuantity(response?.value) > 0}
                shouldDecreaseOnly={false}
                catalogItem={field.catalogItem}
                error={error}
                // A checkbox is over its limit only when none are left, so show Sold Out like the API.
                limitError={getSoldOutLimitError(limitError, isOverCap)}
                onChange={handleBooleanChange}
                {...overrideProps}
            />
        );
    }

    if (field.catalogItem.type === CatalogType.CHOICES) {
        return (
            <ChoicesAddonItem
                selectedValue={response.lineItemTemplate?.id}
                catalogItem={field.catalogItem}
                shouldDecreaseOnly={false}
                shouldShowDefaultImg={shouldShowDefaultImgInAddOnsList}
                error={error}
                onChange={handleChoiceChange}
            />
        );
    }

    return null;
};

export const getInitialValues = (
    form: Form,
    guest: Guest | undefined,
    purchase?: Purchase,
    item?: PurchaseItem | CreatePurchaseItemState,
    isItemLevel?: boolean,
) => {
    const formResponseToUse = isItemLevel ? item?.formResponse : guest?.formResponse;
    const formResponse: FormResponse = {
        user: guest?.formResponse?.user,
        fields: getFields(form, guest ? isPrimaryGuest(guest, purchase, item) : true, isItemLevel)
            .filter((field: Field) =>
                form.organizerOnly ? true : !shouldSkipQuestionnaireModal(field, formResponseToUse, isItemLevel),
            )
            .map((field) => ({
                ...initializeFieldResponse(field),
                value: field.value ?? "",
            })),
    };

    const guestForPopulating = isItemLevel ? { ...guest, formResponse: item?.formResponse } : guest;
    const values = getGuestQuestionnaireFieldResponses((guestForPopulating ?? {}) as Guest, formResponse);

    if (checkIsAutoGeneratedName(values.name)) {
        values.name = "";
    }

    if (values.formResponse) {
        return fixQuestionnaireFormFieldValues(sanitizeFormFieldValues(values));
    }

    return values;
};

const sanitizeFormFieldValues = (guest: Guest) => {
    return produce(guest, (draft) => {
        if (draft.formResponse?.fields) {
            for (const field of draft.formResponse.fields) {
                if (field.type === FieldType.DATE_OF_BIRTH && field.value) {
                    field.value = getISOFormattedDateString(field.value);
                }
            }
        }
    });
};

const validate = (
    questionnaireFields: Field[],
    disableRequiredFields: boolean,
    hasAutoGeneratedName: boolean,
    item?: PurchaseItem | CreatePurchaseItemState,
    isItemLevel?: boolean,
) => {
    return (guest: Guest) => {
        const errors: { name?: string; formResponse?: { fields: Array<{ value: string } | undefined> } } = {};

        // Validate the name field
        if (!disableRequiredFields && !hasAutoGeneratedName && !guest.name.trim()) {
            errors.name = "is required";
        }

        // Use the already-computed rendered field list so hidden fields (e.g. precheck Phone)
        // are never validated, matching exactly what is shown in the modal.
        const formResponseToUse = isItemLevel ? item?.formResponse : guest.formResponse;
        for (const [index, field] of questionnaireFields.entries()) {
            const isRequired = field.required && !disableRequiredFields;
            const fieldValue =
                formResponseToUse?.fields?.find((responseField) => {
                    const isLabelMatch = field.label === responseField.label;
                    if (field.type && responseField.type) {
                        return field.type === responseField.type && isLabelMatch;
                    }
                    return isLabelMatch;
                })?.value ?? "";
            const error = getQuestionnaireFormError(field, fieldValue, isRequired);
            if (error) {
                errors.formResponse = errors.formResponse ?? { fields: [] };
                errors.formResponse.fields[index] = { value: error };
            }
        }

        return errors;
    };
};

export const fieldMap: Partial<Record<FieldType, (props: FieldProps) => JSX.Element>> = {
    [FieldType.WEIGHT]: UnitField,
    [FieldType.HEIGHT]: HeightField,
    [FieldType.DATE_OF_BIRTH]: DateOfBirthField,
    [FieldType.SELECT]: SelectField,
    [FieldType.TEXTAREA]: TextareaField,
    [FieldType.TEXT]: TextField,
    [FieldType.EMAIL]: TextField,
    [FieldType.CHECKBOX]: CheckboxField,
    [FieldType.MARKETING_CONSENT]: RadioField,
    [FieldType.PHONE]: TextField,
};

function isPickupField(field: Field | FieldWithPickupOptions): field is FieldWithPickupOptions {
    return field.type === FieldType.PICKUP;
}

const reorderFormResponseFields = (rawInitialValues: Guest, questionnaireFields: Field[]) => {
    if (!rawInitialValues?.formResponse?.fields) return rawInitialValues;

    const reorderedFields: typeof rawInitialValues.formResponse.fields = [];

    const fieldsByLabel = new Map<string, typeof rawInitialValues.formResponse.fields[0]>();
    for (const field of rawInitialValues.formResponse.fields) {
        fieldsByLabel.set(field.label, field);
    }

    for (const field of questionnaireFields) {
        const matched =
            fieldsByLabel.get(field.label) ??
            (field.type === FieldType.PICKUP
                ? rawInitialValues.formResponse.fields.find((field) => field.type === FieldType.PICKUP)
                : undefined);

        if (matched) {
            reorderedFields.push(matched);
        }
    }

    return {
        ...rawInitialValues,
        formResponse: {
            ...rawInitialValues.formResponse,
            fields: reorderedFields,
        },
    };
};
